import { Logger }    from '../util/logger';
import {
  Path
}                    from './e.m.c.a.404.types';
import { Listener }  from './e.m.c.a.404.events';
import { Node }      from './node';

/**
 *
 * @class Node
 *
 * The event tree is a mirror of the structure being built from the JSON.  This allows us to
 * easily construct a "path" from the root down to a node in the tree and use that as a
 * "selector" to specify which parts of the JSON we are interested in. The application can
 * then just register some number of paths with callbacks so that the parser can ultimately
 * invoke the application's callback when it hits that part of the JSON.
 *
 * This allows us to easily handle even the largest JSON files, because we are only picking out
 * what we need. We don't have to deal with the whole file if we don't want to.
 *
 * The path itself is just a sequence of node names, moving from the root of the tree down
 * to the node of interest.
 *
 * Some ideas for the parsing taken from:
 *
 *   - https://github.com/alemures/fast-json
 *   - https://datastation.multiprocess.io/blog/2022-01-06-analyzing-large-json-files-via-partial-json-parsing.html
 *   - https://github.com/uhop/stream-json
 *   - https://github.com/Collaborne/incremental-json-parser
 *   - https://djedr.github.io/posts/jsonhilo-2021-07-29.html
 *   - https://github.com/xtao-org/jsonhilo
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2023
 *
 */
export class Tree {

  /** the root of the tree */
  root: Node;

  /** standard constructor, provide a root or the name of a root to create */
  constructor(root: Node | string) {

    this.root = typeof root === 'string' ? new Node(root) : root;
  }

  /** add a listener to this node in the tree; creating the nodes along the way if needed. */
  add(path: Path, listener: Listener) {

    let node = this.root;

    for (const pathItem of path) {

      node = node.autoChild(pathItem);
    }

    node.addListeners(listener);
  }

  /** fetch the node at the given path */
  get(path: Path): Node|undefined {

    let node: Node = this.root;

    for (const pathItem of path) {

      const currentNode: Node|undefined = node.child(pathItem);

      if (currentNode === undefined) {

        return undefined;
      }

      node = currentNode;
    }

    return node;
  }

  /** dump the tree */
  toString(): string {

    const ret: string[] = [];
    function it(node: Node, level: number): void {

      const pad = new Array(level).join('-');

      ret.push(`${pad} ${node.name} l:${node.listeners.length}`);

      node.children.forEach((child: Node): void => {

        it(child, level + 2);
      });

    }

    it(this.root, 2);

    return ret.join('\n');
  }

  /** expand the wildcards, but do it in a new/separate tree */
  getCloneExpanded(): Tree {

    const clone = new Tree(this.root.clone());

    this.expandWildcards(clone.root);

    return clone;
  }

  /**
   * When the application uses a wildcard '*' at some point in the path,
   * we have to be able to translate that to the actual paths. "expanding"
   * the wild cards, is just copying the listeners from the wildcard child
   * node to the non-wild card nodes...and we do that all the way down to
   * the leaves.
   *
   * Basically '*' is a glob pattern in the path...it can be used in place
   * of an actual node name.  Because its recursive you can have a '*'
   * anywhere in the path, and you can have as many as you like.
   *
   * @param node the subtree to expand wild cards on.
   *
   * @private
   *
   */
  private expandWildcards(node: Node) {

    const wildNode: Node|undefined = node.child('*');

    if (wildNode !== undefined) {

      node.children.forEach((child: Node): void => {

        if (child.name !== '*') {

          child.addListeners(wildNode.listeners);

          this.mergeNodes(child, wildNode);
        }

      });
    }

    node.children.forEach((child: Node) => this.expandWildcards(child));
  }

  /** merge two subtrees */
  private mergeNodes(target: Node, source: Node): void {

    source.children.forEach((sourceChild: Node): void => {

      const targetChild = target.autoChild(sourceChild.name);

      targetChild.addListeners(sourceChild.listeners);

      this.mergeNodes(targetChild, sourceChild);
    });
  }
}
